import { useState, useEffect, useRef, useCallback } from 'react';

export const useStockfish = (initialMultiPVCount = 3) => {
    const [isReady, setIsReady] = useState(false);
    const [evaluation, setEvaluation] = useState(null);
    const [bestMove, setBestMove] = useState(null);
    const [topMoves, setTopMoves] = useState([]);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [analysisDepth, setAnalysisDepth] = useState(0);
    const [multiPVCount, setMultiPVCountState] = useState(initialMultiPVCount);
    const stockfishRef = useRef(null);
    const analysisTimeoutRef = useRef(null);
    const currentFenRef = useRef(null);
    const currentDepthRef = useRef(22);

    useEffect(() => {
        console.log('Initializing Stockfish engine (multi-threaded)...');

        const stockfish = new Worker('/stockfish/stockfish-nnue-16.js');
        stockfishRef.current = stockfish;

        stockfish.onmessage = (event) => {
            const message = event.data;

            if (message === 'uciok') {
                setIsReady(true);
                console.log('Stockfish ready (multi-threaded)!');
            }

            if (message.startsWith('info')) {
                parseStockfishInfo(message);
            }

            if (message.startsWith('bestmove')) {
                const moveMatch = message.match(/bestmove (\w+)/);
                if (moveMatch) {
                    setBestMove(moveMatch[1]);
                    setIsAnalyzing(false);
                }
            }
        };

        stockfish.postMessage('uci');
        stockfish.postMessage('setoption name UCI_AnalyseMode value true');
        stockfish.postMessage(`setoption name MultiPV value ${initialMultiPVCount}`);
        stockfish.postMessage('setoption name Threads value 4');
        stockfish.postMessage('setoption name Hash value 256');
        stockfish.postMessage('isready');

        return () => {
            console.log('Cleanup Stockfish');
            if (analysisTimeoutRef.current) {
                clearTimeout(analysisTimeoutRef.current);
            }
            stockfish.postMessage('quit');
            stockfish.terminate();
        };
    }, []); // Remove multiPVCount from dependencies - only initialize once

    const parseStockfishInfo = useCallback((message) => {
        const depthMatch = message.match(/depth (\d+)/);
        const scoreMatch = message.match(/score (cp|mate) (-?\d+)/);
        const pvMatch = message.match(/pv (.+)$/);
        const multiPvMatch = message.match(/multipv (\d+)/);
        const nodesMatch = message.match(/nodes (\d+)/);
        const npsMatch = message.match(/nps (\d+)/);

        if (scoreMatch && depthMatch && pvMatch && currentFenRef.current) {
            const depth = parseInt(depthMatch[1]);
            const scoreType = scoreMatch[1];
            let scoreValue = parseInt(scoreMatch[2]);

            // Extract the PV line - this is the string after "pv"
            const pvString = pvMatch[1].trim();
            const pvTokens = pvString.split(' ');

            // Find where the actual moves start (after 'pv')
            // The moves are UCI format like 'e2e4', 'd7d5', etc.
            const pvLine = pvTokens.filter(token => {
                // UCI moves are 4-5 characters (e.g., 'e2e4' or 'e7e8q')
                return token.length >= 4 && token.length <= 5 && /^[a-h][1-8][a-h][1-8][qrbn]?$/.test(token);
            });

            const multiPvIndex = multiPvMatch ? parseInt(multiPvMatch[1]) : 1;
            const nodes = nodesMatch ? parseInt(nodesMatch[1]) : 0;
            const nps = npsMatch ? parseInt(npsMatch[1]) : 0;

            const fenParts = currentFenRef.current.split(' ');
            const sideToMove = fenParts[1];

            if (sideToMove === 'b') {
                scoreValue = -scoreValue;
            }

            let evaluation;

            if (scoreType === 'mate') {
                evaluation = {
                    type: 'mate',
                    value: scoreValue,
                    depth: depth,
                    displayValue: `M${Math.abs(scoreValue)}`,
                    pvLine: pvLine,
                    nodes: nodes,
                    nps: nps
                };
            } else {
                const pawns = scoreValue / 100;
                evaluation = {
                    type: 'cp',
                    value: pawns,
                    depth: depth,
                    displayValue: Math.abs(pawns).toFixed(2),
                    pvLine: pvLine,
                    nodes: nodes,
                    nps: nps
                };
            }

            setAnalysisDepth(depth);

            setTopMoves(prev => {
                const newTopMoves = [...prev];
                // The move is the first element of the PV line
                newTopMoves[multiPvIndex - 1] = {
                    move: pvLine[0] || '',  // FIXED: Use the actual first move from pvLine
                    evaluation: evaluation,
                    index: multiPvIndex
                };
                return newTopMoves.filter(m => m && m.move); // Filter out invalid entries
            });

            if (multiPvIndex === 1) {
                setEvaluation(evaluation);
                setBestMove(pvLine[0] || '');  // FIXED: Use the actual first move
            }
        }
    }, []);

    const analyzePosition = useCallback((fen, depth = 22) => {
        if (!isReady || !stockfishRef.current) {
            console.warn('Stockfish not ready for analysis');
            return;
        }

        console.log('Analyzing position:', fen, 'at depth:', depth);
        currentFenRef.current = fen;
        currentDepthRef.current = depth;
        setIsAnalyzing(true);
        setBestMove(null);
        setEvaluation(null);
        setTopMoves([]);
        setAnalysisDepth(0);

        const stockfish = stockfishRef.current;

        stockfish.postMessage('stop');

        if (analysisTimeoutRef.current) {
            clearTimeout(analysisTimeoutRef.current);
        }

        analysisTimeoutRef.current = setTimeout(() => {
            stockfish.postMessage('ucinewgame');
            stockfish.postMessage(`position fen ${fen}`);
            stockfish.postMessage(`go depth ${depth}`);
        }, 50);
    }, [isReady]);

    const stopAnalysis = useCallback(() => {
        if (stockfishRef.current) {
            console.log('Stopping analysis...');
            stockfishRef.current.postMessage('stop');
            setIsAnalyzing(false);
        }
    }, []);

    // Function to dynamically update MultiPV setting
    const setMultiPV = useCallback((count) => {
        if (!stockfishRef.current || !isReady) {
            console.warn('Stockfish not ready to change MultiPV');
            return;
        }

        console.log('Setting MultiPV to:', count);
        setMultiPVCountState(count);

        const stockfish = stockfishRef.current;

        // Stop current analysis
        stockfish.postMessage('stop');

        // Clear current top moves to avoid stale data
        setTopMoves([]);

        // Update the MultiPV setting
        stockfish.postMessage(`setoption name MultiPV value ${count}`);

        // If we were analyzing, restart analysis with current position
        if (currentFenRef.current && isAnalyzing) {
            if (analysisTimeoutRef.current) {
                clearTimeout(analysisTimeoutRef.current);
            }

            analysisTimeoutRef.current = setTimeout(() => {
                stockfish.postMessage('ucinewgame');
                stockfish.postMessage(`position fen ${currentFenRef.current}`);
                stockfish.postMessage(`go depth ${currentDepthRef.current}`);
            }, 50);
        }
    }, [isReady, isAnalyzing]);

    return {
        isReady,
        evaluation,
        bestMove,
        topMoves,
        analysisDepth,
        isAnalyzing,
        analyzePosition,
        stopAnalysis,
        setMultiPV,
        multiPVCount
    };
};